// @flow

import React, {useMemo} from 'react';
import {
  ButtonBase,
  Grid,
  Typography,
  makeStyles,
  Divider,
} from '@material-ui/core';
import {useHistory} from 'react-router-dom';

import Collapsible from '../../../../elements/Collapsible/Collapsible';
import {colors} from '../../../../assets/styles/colors';
import {AddCircleOutline, Info, LowPriority, Undo} from '@material-ui/icons';

import SortableList, {SortableItem} from 'react-easy-sort';

import {
  DYNAMIC_HOME_PAGE_SECTIONS,
  NEW_BANNER_GROUP_ID,
  STATIC_HOME_PAGE_SECTIONS_LIST,
} from '../../utils/home-page-sections/constants';

import {arrayMove} from '../../../../utils/array';
import LayoutSectionItem from '../../../page-layout/components/LayoutSectionItem';
import StaticSectionItem from './StaticSectionItem';
import {
  useHomePageLayout,
  useHomePageLayoutActions,
} from '../../../page-layout/utils/home-page';

import {useIsFeatureInTier} from '../../../../utils/component';
import TiendadaTooltip from '../../../../elements/tiendada-tooltip';
import CustomChip from '../../../../elements/CustomChip';

import type {HomePageAddableSection} from '../../utils/home-page-sections/constants';
import {useStorePromotions} from '../../../marketing/promotions/utils';
import {useCategories} from '../../../categories/utils';
import {useFeaturedProducts} from '../../../products/utils/hooks';
import {useStoreReviews} from '../../../marketing/reviews/utils';
import {useBlogPostList} from '../../../blog/utils/blog-post';
import {useDeliveryShippingConfig} from '../../../configure/logistics/logistics/utils';
import {useStoreUiInformation} from '../../../ui/utils';
import {useTierRestrictionActions} from '../../../TierRestriction/utils';
import PremiumFeatureBadge from '../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import UpgradeIcon from '../../../../elements/icons/UpgradeIcon';
import {useFaqQuestion} from '../../../configure/legal/utils/useStorePoliciesAndPrivacy';
import {useStoreBusinessModel} from '../../../store/utils';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'sticky',
    top: '80px',
  },
  customCollapsibleTop: {
    marginTop: '0!important',
    borderRadius: '0 0 .75rem .75rem !important',
  },
  customCollapsibleBottom: {
    marginTop: '0!important',
    borderRadius: '.75rem .75rem 0 0!important',
  },
  addSectionBtn: {
    border: `1px solid ${colors.grey300}`,
    borderRadius: 12,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:disabled': {
      opacity: 0.5,
    },
  },
  itemOptions: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  subtitle: {
    fontWeight: 500,
    fontSize: 14,
    color: colors.grey600,
    textTransform: 'uppercase',
  },
  divider: {
    margin: '1rem 0',
  },
  toggleBtn: {
    background: colors.grey200,
    borderRadius: 12,
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textDecoration: 'none',
    padding: theme.spacing(1.5),
    color: colors.grey800,
  },
  deleteIcon: {
    color: colors.grey600,
  },
  label: {
    color: colors.grey800,
    fontWeight: 500,
    fontSize: 14,
  },
  deleteButton: {
    color: colors.danger,
  },
  successIcon: {
    color: colors.success,
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    userSelect: 'none',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '1rem',
  },
  switchContainer: {
    width: '100%',
    padding: '1rem 1rem 1rem 0',
    display: 'flex',
    background: 'white',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '.75rem',
    marginBottom: '1rem',
    paddingInline: '1rem',
    gap: '.5rem',
  },
  switchLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: colors.grey800,
  },
}));

export type AddableHomePageSections = {
  label: string,
  name: string,
};

type Props = {
  starClosed?: boolean,
};

export default function HomeLayoutSectionList({starClosed}: Props) {
  const classes = useStyles();

  const history = useHistory();

  const homePageLayout = useHomePageLayout();

  const businessModel = useStoreBusinessModel();

  const hasSortSectionFeature = useIsFeatureInTier('STORE_PAGE_LAYOUT');
  const hasBannerGroupFeature = useIsFeatureInTier('BANNER_GROUP');

  const {showTierRestrictionModal} = useTierRestrictionActions();

  const {
    toggleEnableSection,
    toggleEnableHomePageLayout,
    loading,
    sortSections,
  } = useHomePageLayoutActions();

  const sections = homePageLayout?.layout || [];

  const storeUiInformation = useStoreUiInformation();

  const promotions = useStorePromotions();
  const categories = useCategories();
  const featuredProducts = useFeaturedProducts();
  const reviews = useStoreReviews();
  const blogPosts = useBlogPostList();
  const {faqQuestions} = useFaqQuestion();
  const {logisticShippingDeliveryConfig} = useDeliveryShippingConfig();

  const addableSection: HomePageAddableSection[] = useMemo(() => {
    const bannerGroupDisabled =
      sections.filter(
        section =>
          section.pageSectionType ===
          DYNAMIC_HOME_PAGE_SECTIONS.STORE_BANNER.name
      ).length >= 5;

    const bannerGroupTitle = (
      <>
        Banners
        {!hasBannerGroupFeature && <PremiumFeatureBadge />}
      </>
    );

    const sectionsToAdd = [
      {
        title: bannerGroupTitle,
        name: DYNAMIC_HOME_PAGE_SECTIONS.STORE_BANNER.name,
        disabled: bannerGroupDisabled || !hasBannerGroupFeature,
        onAdd: () => {
          history.push(
            `/my-store/pages/home-page/banner/${NEW_BANNER_GROUP_ID}/new`
          );
        },
      },
    ];

    sectionsToAdd.push({
      title: 'Informacional',
      name: DYNAMIC_HOME_PAGE_SECTIONS.STORE_INFORMATION_BLOCK.name,
      disabled: false,
      onAdd: () => {
        history.push('/my-store/pages/home-page/information-block/new');
      },
    });

    return sectionsToAdd;
  }, [hasBannerGroupFeature, history, sections]);

  if (!homePageLayout) return null;

  const sortSectionEnabled = !!homePageLayout?.enabled && hasSortSectionFeature;

  const handleToggleEnableHomePageLayout = () => {
    if (!hasSortSectionFeature) {
      showTierRestrictionModal();
      return;
    }
    toggleEnableHomePageLayout();
  };

  return (
    <div className={classes.root}>
      <ButtonBase
        className={classes.switchContainer}
        onClick={handleToggleEnableHomePageLayout}
        disabled={loading}
      >
        {sortSectionEnabled ? <Undo /> : <LowPriority />}

        <span className={classes.switchLabel}>
          {sortSectionEnabled
            ? 'Regresar al orden predeterminado'
            : 'Personalizar orden'}
        </span>
      </ButtonBase>

      <Collapsible
        divisible={false}
        title="Agregar Nuevas Secciones"
        startClosed={starClosed}
        className={classes.customCollapsibleBottom}
      >
        <Grid container spacing={1}>
          {addableSection.map((section, idx) => (
            <Grid key={`${section.name}-${idx}`} item xs={12}>
              <ButtonBase
                onClick={section.onAdd}
                className={classes.addSectionBtn}
                disabled={section.disabled}
              >
                <Typography className={classes.label}>
                  {section.title}
                </Typography>
                <span className={classes.deleteIcon}>
                  <AddCircleOutline fontSize="small" />
                </span>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Collapsible>

      <Collapsible
        divisible={false}
        title="Secciones Agregadas"
        startClosed={starClosed}
        className={classes.customCollapsibleTop}
      >
        <div className={classes.titleContainer}>
          <Typography className={classes.subtitle}>
            Secciones Dinámicas
            {!hasSortSectionFeature && (
              <CustomChip
                label={
                  <>
                    Mejorar <UpgradeIcon width={18} height={18} />
                  </>
                }
                color="primary"
                size="small"
              />
            )}
          </Typography>
          <TiendadaTooltip
            title="Arrastra y suelta para cambiar el orden de las secciones"
            placement="top-end"
          >
            <Info />
          </TiendadaTooltip>
        </div>

        <section className={classes.sectionList}>
          <SortableList
            className={classes.sectionList}
            draggedItemClassName="dragged"
            onSortEnd={(oldIndex: number, newIndex: number) => {
              const sortedSections = arrayMove(
                homePageLayout.layout,
                oldIndex,
                newIndex
              );

              sortSections(sortedSections);
            }}
            allowDrag={hasSortSectionFeature && sortSectionEnabled}
          >
            {homePageLayout.layout.map((section, index) => {
              const sectionInformation =
                DYNAMIC_HOME_PAGE_SECTIONS[section.pageSectionType];

              const key =
                section.pageSectionType + section.sectionIdentifier || '';

              if (!sectionInformation) return null;

              let sectionEnabled = section.enabled;
              let emptyContent;
              let tooltipContent: string = '';

              if (section.pageSectionType === 'STORE_PROMOTIONS') {
                emptyContent = !(
                  promotions.length > 0 && promotions.some(p => p.enabled)
                );
                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay promociones activas, crea una para activar esta sección';
                }
              }

              if (
                section.pageSectionType === 'PRODUCT_CATEGORY' ||
                section.pageSectionType === 'PRODUCT_CATEGORY_AISLE'
              ) {
                emptyContent = !categories?.length;
                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay categorías activas, necesitas crear una para activar esta sección';
                }
              }

              if (section.pageSectionType === 'FEATURED_PRODUCTS') {
                emptyContent = !featuredProducts?.length;
                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent = 'No hay elementos destacados';
                }
              }

              if (section.pageSectionType === 'ORDER_REVIEWS') {
                emptyContent =
                  !reviews || !reviews.some(r => r.enabled && r.featured);

                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay reseñas destacadas, necesitas más de 3 reseñas destacadas para activar esta sección';
                }
              }

              if (section.pageSectionType === 'BLOG') {
                emptyContent = !blogPosts.some(post => post.featured);
                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay artículos destacados, necesitas destacar al menos un artículo para activar esta sección';
                }
              }

              if (section.pageSectionType === 'SHIPPING_PROMOTION') {
                emptyContent = !(
                  logisticShippingDeliveryConfig &&
                  logisticShippingDeliveryConfig.enabledPromotions
                );
                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay promociones de envío activas, necesitas crear una para activar esta sección';
                }
              }

              if (section.pageSectionType === 'STORE_PROFILE') {
                emptyContent =
                  !storeUiInformation ||
                  !storeUiInformation?.profileInformation?.description ||
                  !storeUiInformation?.profileInformation?.profilePhotoId;

                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay descripción o foto de perfil, necesitas completar la información de tu tienda para activar esta sección';
                }
              }

              if (section.pageSectionType === 'FAQ') {
                emptyContent = !faqQuestions?.length;
                if (emptyContent) {
                  sectionEnabled = false;
                  tooltipContent =
                    'No hay preguntas frecuentes, necesitas crear una para activar esta sección';
                }
              }

              return (
                <SortableItem key={key}>
                  <LayoutSectionItem
                    enabled={sortSectionEnabled}
                    id={section.sectionIdentifier || section.pageSectionType}
                    onToggleSectionEnable={() => {
                      toggleEnableSection(
                        section.pageSectionType,
                        section.sectionIdentifier
                      );
                    }}
                    sectionEnabled={sectionEnabled}
                    title={sectionInformation.label}
                    emptySection={emptyContent}
                    toggleDisabled={!sortSectionEnabled || emptyContent}
                    tooltipLabel={tooltipContent}
                  />
                </SortableItem>
              );
            })}
          </SortableList>
        </section>
        <Divider className={classes.divider} />
        <div className={classes.titleContainer}>
          <Typography className={classes.subtitle}>
            Secciones Estáticas
          </Typography>
        </div>
        <section className={classes.sectionList}>
          {STATIC_HOME_PAGE_SECTIONS_LIST.filter(item =>
            item.storeBusinessModels.includes(businessModel)
          )
            .filter(({hide}) => !hide)
            .map(({label, name, id}) => (
              <StaticSectionItem key={id} id={id} label={label} />
            ))}
        </section>
      </Collapsible>
    </div>
  );
}
